/* eslint-disable no-console */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import has from "lodash.has";
import includes from "lodash.includes";
import pluck from "just-pluck-it";
import get from "just-safe-get";

import { setupSentryUser } from "@/utils/tracking";
import {
  onLogin,
  AUTH_TOKEN_STORAGE_ITEM,
  removeAuthToken,
} from "@/lib/apollo/apollo.js";

// Create a User Pool Object
const poolData = {
  UserPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.VUE_APP_COGNITO_APP_CLIENT_ID,
};
const userPool = new CognitoUserPool(poolData);

// Authenticate with a username and password
export const usernamePasswordAuth = ({ Username, Password, apolloClient }) => {
  // Store the username in local storage for when we need to get a refresh token...
  localStorage.setItem("pai-un", Username);

  const authenticationData = {
    Username,
    Password,
  };

  const authenticationDetails = new AuthenticationDetails(authenticationData);

  const userData = {
    Username,
    Pool: userPool,
  };

  const cognitoUser = new CognitoUser(userData);

  return new Promise(function (resolve, reject) {
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async () => {
        try {
          const accessToken = await getCognitoToken();
          await onLogin(apolloClient, accessToken);
          resolve();
        } catch (error) {
          reject(error);
        }
      },

      onFailure: (err) => {
        console.error("usernamePasswordAuth:", err);

        reject(err);
      },
    });
  });
};

// Authenticating a user with a passwordless custom flow.
export const customAuth = ({ shopName, challengeToken, apolloClient }) => {
  const Username = `${shopName}@myshopify.com`;

  // Store the shop name in local storage for when we need to get a refresh token...
  localStorage.setItem("pai-un", Username);

  // Setup our authentication data...
  const authenticationData = {
    Username,
  };

  // Create a new AuthenticationDetails instance...
  const authenticationDetails = new AuthenticationDetails(authenticationData);

  // Setup our user data...
  const userData = {
    Username,
    Pool: userPool,
  };

  // Create a new CognitoUser instance with our user data...
  const cognitoUser = new CognitoUser(userData);

  // Set the authentication flow type...
  cognitoUser.setAuthenticationFlowType("CUSTOM_AUTH");

  return new Promise(function (resolve, reject) {
    // Initiate the Cognito auth...
    cognitoUser.initiateAuth(authenticationDetails, {
      onSuccess: async () => {
        // If we succeed then...
        try {
          // Try to get a cognito access token...
          const accessToken = await getCognitoToken();
          // Call onLogin to reset the apollo store and store the access token...
          await onLogin(apolloClient, accessToken);

          resolve();
        } catch (error) {
          reject(error);
        }
      },

      onFailure: (err) => {
        console.error("customAuth:", err);

        reject(err.message);
      },

      customChallenge: function () {
        // Respond to the Cognito custom challenge...
        cognitoUser.sendCustomChallengeAnswer(challengeToken, this);
      },
    });
  });
};

export const refreshAccessToken = async () => {
  // Get the shop name from local storage...
  const Username = localStorage.getItem("pai-un");
  let refreshToken;

  // Return false if we don't have a username...
  if (!Username) return false;

  // Get the refreshToken...
  try {
    refreshToken = await getCognitoToken("refreshToken");
  } catch (error) {
    // Log the error to Sentry if we can't get the refreshToken...
    window.Sentry && window.Sentry.captureException(error);
  }

  // Setup our user data...
  const userData = {
    Username,
    Pool: userPool,
  };

  // Create a cognito user...
  const cognitoUser = new CognitoUser(userData);

  // Create a cognito refresh token...
  const token = new CognitoRefreshToken({ RefreshToken: refreshToken });

  return new Promise(function (resolve, reject) {
    // Refresh the session...
    return cognitoUser.refreshSession(token, (err, session) => {
      // Handle any errors...
      if (err) {
        console.error(
          "refreshAccessToken:",
          err.message || JSON.stringify(err)
        );

        reject(err.message);
      }

      resolve(session?.accessToken?.jwtToken);
    });
  });
};

export const isAuthenticated = () => {
  // Get the auth token from...
  const authToken = window.localStorage.getItem(AUTH_TOKEN_STORAGE_ITEM);
  // Check that authToken exists and it's not an empty string...
  return (
    authToken !== null &&
    typeof authToken === "string" &&
    authToken.length !== 0
  );
};

export const isAuthError = (graphQLErrors) => {
  // Look for all the errorTypes in the graphQLErrors array...
  const errorTypes = pluck(graphQLErrors, "errorType");

  // Check to see if any of the errors are an UnauthorizedException
  return includes(errorTypes, "UnauthorizedException");
};

export const setupSession = () => {
  // Get the current user...
  const cognitoUser = userPool.getCurrentUser();

  // Check there is an actual user object...
  if (!cognitoUser) return false;

  cognitoUser.getSession((err, session) => {
    if (err) {
      console.error("setupSession:", err.message || JSON.stringify(err));
    }

    // If the session is valid perform setup tasks...
    if (session?.isValid()) {
      const id = getAccountId();

      setupSentryUser({ id, username: cognitoUser.username });
    } else {
      return false;
    }
  });
};

export const getCognitoToken = function (tokenType = "accessToken") {
  const cognitoUser = userPool.getCurrentUser();

  if (cognitoUser != null) {
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => {
        if (err) {
          console.error("getCognitoToken:", err.message || JSON.stringify(err));
          return reject(err);
        }

        // Set the token child prop based on whether we want an accessToken or refreshToken...
        const tokenChildProp =
          tokenType === "accessToken" ? "jwtToken" : "token";
        // Check whether the token exists...
        const token = get(session, `${tokenType}.${tokenChildProp}`);

        resolve(token);
      });
    });
  }
};

export const getAccountId = () => {
  const cognitoUser = userPool.getCurrentUser();

  if (cognitoUser !== null && typeof cognitoUser.getSession === "function") {
    try {
      return cognitoUser.getSession((err) => {
        if (err) {
          throw err;
        }

        // Get the user ID...
        return (
          cognitoUser?.signInUserSession?.accessToken?.payload?.sub ?? null
        );
      });
    } catch (error) {
      console.error("getAccountId:", error.message || JSON.stringify(error));
    }
  } else {
    return null;
  }
};

export const unauthenticate = async (redirect = true) => {
  // Get the current user
  const cognitoUser = userPool.getCurrentUser();

  // Check there is an actual user object and sign out of Cognito
  if (cognitoUser !== null) cognitoUser.signOut();

  // Remove the shop name from local storage...
  localStorage.removeItem("pai-un");

  removeAuthToken();

  // Redirect the user to the login route...
  // This will also reset the apollo store
  if (redirect) window.document.location.href = "/login";
};
